import React from "react"
import { Inbox } from "@novu/react"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectUser } from "~/ui-rtk/app/selectors/user.selector"
import { useNovuHmac } from "~/ui-rtk/hooks/use-novu-hmac"
import {
  JobNotificationCard,
  NovuNotificationLike,
} from "./JobNotificationCard"

export const NotificationInbox: React.FC = () => {
  const navigate = useNavigate()
  const { id: subscriberId } = useAppSelector(selectUser)
  // Hardcoded fallback so the inbox works locally even when
  // VITE_NOVU_APPLICATION_IDENTIFIER isn't loaded into the dev server. Deployed
  // builds still override this via the env var.
  const applicationIdentifier = "6q3f3X0QD2IM"
  const { data: hmacData, isLoading, error } = useNovuHmac()

  if (!subscriberId) {
    return <div></div>
  }

  if (isLoading) {
    return (
      <div className="w-6 h-6 animate-pulse bg-gray-300 rounded-full"></div>
    )
  }

  if (error) {
    console.error("Failed to load Novu HMAC:", error)
    return <div></div>
  }

  if (!hmacData?.hmacHash) {
    return <div></div>
  }

  return (
    <Inbox
      applicationIdentifier={applicationIdentifier}
      subscriberId={subscriberId}
      subscriberHash={hmacData.hmacHash}
      // "Jobs" surfaces background work (e.g. ad blasts) in the header feed:
      // started + completed/failed events. Relies on those Novu workflows being
      // tagged `jobs` (see the `ad-blast` workflow). The first tab is unfiltered.
      tabs={[{ label: "All" }, { label: "Jobs", filter: { tags: ["jobs"] } }]}
      // Custom rich card per notification (status badge, progress bar, counts).
      // Reads notification.data when the workflow maps the payload; otherwise
      // falls back to subject/body + inferred status.
      renderNotification={notification => (
        <JobNotificationCard
          notification={notification as unknown as NovuNotificationLike}
          onNavigate={url => navigate(url)}
        />
      )}
      // SPA navigation for any built-in redirects (keeps it client-side).
      routerPush={path => navigate(path)}
      appearance={{
        variables: {
          colorPrimary: "#eb89b5",
          colorPrimaryForeground: "#FFFFFF",
          colorSecondary: "#121826",
          colorSecondaryForeground: "#F8FAFC",
          colorCounter: "#E2323E",
          colorCounterForeground: "#FFFFFF",
          colorBackground: "#05051E",
          colorRing: "#eb89b5",
          colorForeground: "#F8FAFC",
          colorNeutral: "#677489",
          colorShadow: "rgba(0, 0, 0, 0.15)",
          fontSize: "0.9rem",
        },
        elements: {
          bellIcon: {
            color: "#F8FAFC",
          },
          popoverContent: {
            backgroundColor: "#212936",
            borderColor: "#222446",
          },
          notificationList: {
            backgroundColor: "#121826",
            borderColor: "#222446",
          },
          button: {
            backgroundColor: "#eb89b5",
            color: "#FFFFFF",
          },
        },
      }}
      placement="bottom-end"
      placementOffset={{
        mainAxis: 10,
        crossAxis: 10,
      }}
    />
  )
}
